/**
 * Pending-writes tracker — keeps the UI consistent with ASA without
 * waiting for the 60-second staleTime or a manual page refresh.
 *
 * Flow:
 *  1. Every Ads Manager mutation returns a `PendingWriteHandle` with
 *     a `pending_write_id`. Mutation hooks (or page-level fanout
 *     handlers) call `trackPendingWrite(id, type, onResolve?)` to
 *     register the id and an optional callback.
 *  2. While there's anything tracked, the global poller hits
 *     `GET /api/pending-writes/state` every POLL_INTERVAL_MS.
 *  3. When the drainer flips an id to `synced` / `error`, the tracker
 *     fires the per-id callback (so callers can wait for ASA
 *     confirmation before showing a toast) and invalidates the
 *     matching React Query prefix so the table re-fetches.
 *  4. Polling stops when the registry empties OR after
 *     MAX_POLL_DURATION_MS (safety cap so a stuck pending write
 *     doesn't keep us hammering forever — at the cap we fire the
 *     callback with `{status: "timeout"}` so the caller can still
 *     surface a useful message).
 *
 * Why polling and not Server-Sent Events: SSE needs ticket auth and
 * long-lived asyncpg LISTEN connections behind caddy — bigger lift.
 * Polling at 1.5s gives ~1.5s p95 latency end-to-end (drainer
 * finishes in 1-3s), which beats the previous 4s+12s blind refetch
 * and is good enough for an interactive dashboard.
 */

import type { QueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "./api";
import {
  createCoalescedInvalidator,
  type CoalescedInvalidator,
} from "./coalesced-invalidator";
import { useCanWriteApp } from "./useAuth";

export type ResourceType =
  | "campaign"
  | "ad_group"
  | "keyword"
  | "negative_keyword"
  // Заявки такого типа рождает Apply CPP. Тип обязан совпадать с
  // Literal схемы на бэкенде: там он есть, и союз, который его не
  // знает, молча проглотит подтверждение, ничего не перерисовав.
  | "ad";

type PendingWriteConfirmed = {
  id: number;
  resource_type: ResourceType;
};

type PendingWriteState = {
  id: number;
  resource_type: ResourceType;
  resource_id: number | null;
  operation:
    | "create"
    | "update"
    | "pause"
    | "enable"
    | "delete"
    | "duplicate";
  status: "pending" | "in_flight" | "synced" | "error";
  error_message: string | null;
  retry_count: number;
};

/** Outcome a registered onResolve callback receives. */
export type ResolveResult =
  | { status: "synced" }
  | { status: "error"; errorMessage: string | null }
  | { status: "timeout" };

type Tracked = {
  id: number;
  /** When did we start tracking — used for the global timeout. */
  startedAt: number;
  /** Hint from the mutation site so we know which query to invalidate
   *  when this id resolves. The endpoint also returns resource_type
   *  but having a client-side fallback avoids a race where the row
   *  isn't visible yet (writer transaction) on the first poll. */
  resourceType: ResourceType;
  /** Optional caller-supplied callback fired exactly once when the
   *  drainer reports synced / error, or when MAX_POLL_DURATION_MS
   *  elapses (timeout). After calling it the entry is removed. */
  onResolve?: (result: ResolveResult) => void;
};

const POLL_INTERVAL_MS = 1500;
// 90s covers the long tail of cascade operations like
// op="duplicate" — the campaign cascade (campaign + ad_groups +
// keywords + negatives + ads) can hit 30+ seconds for keyword-
// heavy sources. Real prod sample: pw#417 (PM_TR_Exact_CPA_Bid)
// finished at 31s — JUST over the previous 30s cap, so the
// frontend showed a false-negative "taking longer than usual"
// toast even though the cascade succeeded. Inline edits + bulk
// edits typically resolve in 1-3s; the new ceiling only matters
// for the edge cases.
const MAX_POLL_DURATION_MS = 90_000;

/** Фоновый опрос: «что подтвердил Apple по организации».
 *
 *  Отдельный темп от POLL_INTERVAL_MS, и это принципиально. Полторы
 *  секунды — цена ожидания СВОЕЙ мутации, она живёт секунды. Фоновый
 *  опрос идёт всегда, пока открыта вкладка, и на 1,5 с это были бы
 *  сорок запросов в минуту впустую. Пятнадцать секунд при замеренных
 *  0,13 мс на запрос — 10 мс серверной работы в минуту на вкладку. Для
 *  сравнения: `refetchInterval` на таблице стоил бы 144 мс в минуту, и
 *  платились бы они даже когда не происходит ничего. */
const AMBIENT_POLL_MS = 15_000;

/** Окно шире шага опроса намеренно: заявка, подтверждённая между двумя
 *  опросами, не должна провалиться. Перекрытие даёт повторы, их
 *  отсеивает `ambientSeen`. */
const AMBIENT_WINDOW_S = 45;

/** Идентификаторы, по которым кеш уже сброшен, со временем встречи.
 *  Живут чуть дольше окна опроса — дальше повториться не могут. */
const ambientSeen = new Map<number, number>();

function rememberSeen(id: number): void {
  ambientSeen.set(id, Date.now());
}

function forgetStaleSeen(): void {
  const cutoff = Date.now() - AMBIENT_WINDOW_S * 1000 * 3;
  for (const [id, at] of Array.from(ambientSeen.entries())) {
    if (at < cutoff) ambientSeen.delete(id);
  }
}

const tracked = new Map<number, Tracked>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of listeners) fn();
}

/**
 * Register a pending_write returned by a mutation. After this call,
 * the tracker will poll until the write resolves and then invalidate
 * the relevant React Query prefix.
 *
 * If `onResolve` is provided, it's called exactly once when the
 * drainer reports synced / error, or when the global timeout fires.
 * Use this when you want to delay a UI affordance (e.g. a success
 * toast) until ASA has actually confirmed the write — so users see
 * "paused" only when ASA replied with the expected state, not when
 * our API merely accepted the request.
 */
export function trackPendingWrite(
  pendingWriteId: number,
  resourceType: ResourceType,
  onResolve?: (result: ResolveResult) => void,
): void {
  if (!pendingWriteId || pendingWriteId <= 0) {
    // No id to track — fire the callback synchronously with synced
    // so callers don't get stuck waiting on a write that never
    // produced an id (e.g. a no-op update).
    onResolve?.({ status: "synced" });
    return;
  }
  const existing = tracked.get(pendingWriteId);
  if (existing) {
    // De-dup: keep the existing entry but compose any new callback
    // alongside the previous one. Both callers expect to be told.
    if (onResolve) {
      const prev = existing.onResolve;
      existing.onResolve = (result) => {
        prev?.(result);
        onResolve(result);
      };
    }
    return;
  }
  tracked.set(pendingWriteId, {
    id: pendingWriteId,
    startedAt: Date.now(),
    resourceType,
    onResolve,
  });
  notify();
}

/** Mount once at app root. Polls while anything's tracked, idle when not. */
export function usePendingWritesTracker(): {
  /** Live count of unresolved writes — drives an optional UI indicator. */
  pendingCount: number;
} {
  const qc = useQueryClient();
  const [count, setCount] = useState(tracked.size);

  // Subscribe to the registry so the indicator count is live.
  useEffect(() => {
    const update = () => setCount(tracked.size);
    listeners.add(update);
    return () => {
      listeners.delete(update);
    };
  }, []);

  // The poller. Idle when tracked is empty — `count > 0` triggers it.
  useEffect(() => {
    if (count === 0) return;
    let cancelled = false;

    const tick = async () => {
      if (cancelled || tracked.size === 0) return;
      // First, drop anything past the timeout client-side and fire
      // the callbacks with `timeout`. This protects callers who are
      // waiting for an ASA reply that never comes (drainer stuck on
      // retry, ASA outage, etc).
      const now = Date.now();
      const timedOutTypes = new Set<ResourceType>();
      for (const [id, t] of Array.from(tracked.entries())) {
        if (now - t.startedAt > MAX_POLL_DURATION_MS) {
          tracked.delete(id);
          timedOutTypes.add(t.resourceType);
          t.onResolve?.({ status: "timeout" });
        }
      }
      if (timedOutTypes.size > 0) {
        scheduleInvalidate(qc, timedOutTypes);
      }
      if (tracked.size === 0) {
        notify();
        return;
      }

      const idList = Array.from(tracked.keys());
      let resolved: PendingWriteState[] = [];
      try {
        const { data } = await api.get<PendingWriteState[]>(
          "/api/pending-writes/state",
          { params: { ids: idList.join(",") } },
        );
        resolved = data;
      } catch {
        // Network blip — keep polling.
      }

      const invalidatedTypes = new Set<ResourceType>();
      for (const pw of resolved) {
        if (pw.status !== "synced" && pw.status !== "error") continue;
        const entry = tracked.get(pw.id);
        if (!entry) continue;
        tracked.delete(pw.id);
        // Фоновый опрос увидит эту же заявку в своём окне —
        // помечаем, чтобы он не сбросил кеш второй раз.
        rememberSeen(pw.id);
        invalidatedTypes.add(entry.resourceType);
        if (pw.status === "synced") {
          // `synced` значит «запрос выполнен», а не «каждый элемент в нём
          // удался»: сливщик пишет сюда строку, когда сделано НЕ ВСЁ.
          //
          // Частичный исход приезжает как ОШИБКА намеренно, хотя строго
          // говоря он не ошибка — зеркало обновлено, запросы перечитаны.
          // Отдельный четвёртый исход пришлось бы обработать в каждом из
          // четырёх мест, где разбор идёт через if/else: там он молча упал
          // бы в `else` и посчитался таймаутом, а компилятор бы промолчал.
          // Ошибкой он обрабатывается везде и сразу, и проглотить его
          // нельзя. Сама строка говорит, что именно сделано: «Done 7 of 9».
          entry.onResolve?.(
            pw.error_message
              ? { status: "error", errorMessage: pw.error_message }
              : { status: "synced" },
          );
        } else {
          entry.onResolve?.({
            status: "error",
            errorMessage: pw.error_message,
          });
        }
      }

      if (invalidatedTypes.size > 0) {
        scheduleInvalidate(qc, invalidatedTypes);
      }
      notify();

      if (tracked.size > 0) {
        timeoutId = window.setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    let timeoutId = window.setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
    // We deliberately depend only on `count` going non-zero to start
    // — once running, the poll loop keeps itself alive until the map
    // empties. Re-running on every count change would shuffle timers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count > 0]);

  // ★ Роут сообщает о ПРАВКАХ, и права на него — это права писать. У
  // просмотрщика их нет, ответ известен заранее, и спрашивать сервер о
  // том, на что ответ известен, незачем: он честно отвечает 403, а в
  // консоли остаётся ошибка, которую человеку нечем объяснить.
  //
  // `null` — «умеет писать хоть куда-то»: обновлять нужно ту вкладку,
  // где человек что-то меняет, а какое именно приложение открыто, здесь
  // неизвестно и не важно.
  const mayPoll = useCanWriteApp(null);

  // Фоновый опрос — единственный канал, по которому вкладка узнаёт об
  // изменениях, сделанных НЕ ею: правила, пресеты, автопилот. Своих
  // заявок у браузера в этом случае нет, регистрировать в `tracked`
  // нечего, а сама по себе вкладка не перезапрашивает никогда
  // (`staleTime` 60 с, `refetchOnWindowFocus: false`).
  useEffect(() => {
    if (!mayPoll) return;
    let cancelled = false;
    let timeoutId = 0;

    const tick = async () => {
      if (cancelled) return;
      // Скрытая вкладка не опрашивает вовсе: показывать там нечего, а
      // свёрнутых вкладок у человека обычно больше, чем открытых.
      if (document.visibilityState === "visible") {
        try {
          const { data } = await api.get<PendingWriteConfirmed[]>(
            "/api/pending-writes/recent",
            { params: { window_s: AMBIENT_WINDOW_S } },
          );
          const fresh = new Set<ResourceType>();
          for (const pw of data) {
            // Заявку, которую вкладка ждёт сама, оставляем локальному
            // циклу: у него есть колбэк вызывающего (тост, снятие
            // выделения), а здесь его нет.
            if (tracked.has(pw.id)) continue;
            if (ambientSeen.has(pw.id)) continue;
            rememberSeen(pw.id);
            fresh.add(pw.resource_type);
          }
          if (fresh.size > 0) scheduleInvalidate(qc, fresh);
          forgetStaleSeen();
        } catch (err) {
          // ★ Отказ в правах и обрыв сети — разные вещи, и раньше их
          // ловил один пустой catch. Роль без доступа к этому роуту
          // получала 403 каждые 15 секунд, на каждой странице, вечно:
          // в консоли поток ошибок, на сервере поток отказов, и ни одна
          // из сторон не могла это прекратить.
          //
          // 403 — прав нет и не появится до перезагрузки. 401 — сессии
          // нет: обновление токена делает сам клиент, и если отказ дошёл
          // сюда, обновляться больше нечем. В обоих случаях опрашивать
          // дальше бессмысленно.
          const status = (err as { response?: { status?: number } })?.response
            ?.status;
          if (status === 401 || status === 403) {
            cancelled = true;
            // Один раз, а не на каждом тике: сообщение важно увидеть,
            // а не утопить в повторах.
            console.warn(
              "pending-writes: фоновое обновление выключено, "
                + `сервер ответил ${status}`,
            );
            return;
          }
          // Всё остальное — обрыв сети, перезапуск сервера, таймаут.
          // Продолжаем опрашивать: это состояние проходит само.
        }
      }
      if (!cancelled) timeoutId = window.setTimeout(tick, AMBIENT_POLL_MS);
    };

    // Возврат к вкладке — повод спросить сразу, а не досиживать шаг:
    // человек вернулся именно затем, чтобы посмотреть.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      window.clearTimeout(timeoutId);
      void tick();
    };
    document.addEventListener("visibilitychange", onVisible);

    timeoutId = window.setTimeout(tick, AMBIENT_POLL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [qc, mayPoll]);

  return { pendingCount: count };
}

/** Wait for a batch of pending writes to all resolve. Resolves with
 *  per-id outcomes when every id in `handles` has reported back
 *  (synced / error / timeout).
 *
 *  Useful for bulk fanouts where the page wants to show a single
 *  summary toast only after every ASA reply has landed — instead of
 *  flashing one for each PATCH 202 response. */
export function awaitPendingWrites(
  handles: ReadonlyArray<{ id: number; resourceType: ResourceType }>,
): Promise<Map<number, ResolveResult>> {
  return new Promise((resolve) => {
    if (handles.length === 0) {
      resolve(new Map());
      return;
    }
    const results = new Map<number, ResolveResult>();
    for (const { id, resourceType } of handles) {
      trackPendingWrite(id, resourceType, (res) => {
        results.set(id, res);
        if (results.size === handles.length) {
          resolve(results);
        }
      });
    }
  });
}

/** End-to-end "fire mutation, wait for ASA, toast the result" helper.
 *
 *  Used for inline single-row edits (bid, daily_budget, target_cpa,
 *  default_bid, cpa_goal). The mutation itself returns 202 from our
 *  API; the ASA confirmation lands 1-3 seconds later via the drainer.
 *  This helper:
 *    1. awaits the mutation Promise (catching pre-ASA failures —
 *       4xx/5xx from our backend, network errors, etc).
 *    2. registers the returned pending_write_id with the tracker.
 *    3. awaits the per-id resolution (synced / error / timeout).
 *    4. fires `flash` with a concrete success or failure message.
 *
 *  No toast is shown for the in-flight state — the user only sees a
 *  message once ASA has actually replied with the expected outcome.
 */
export async function runWithAsaConfirmToast(args: {
  mutation: Promise<{ pending_write_id: number; resource_type?: string }>;
  resourceType: ResourceType;
  fieldLabel: string;
  flash: (msg: string, variant: "success" | "error") => void;
}): Promise<void> {
  const { mutation, resourceType, fieldLabel, flash } = args;
  let handle: { pending_write_id: number };
  try {
    handle = await mutation;
  } catch (e) {
    flash(
      `${fieldLabel} update failed: ${e instanceof Error ? e.message : String(e)}`,
      "error",
    );
    return;
  }
  if (!handle.pending_write_id || handle.pending_write_id <= 0) {
    // Backend returned `synced` directly (no-op edit). Treat as success.
    flash(`${fieldLabel} updated on ASA`, "success");
    return;
  }
  const results = await awaitPendingWrites([
    { id: handle.pending_write_id, resourceType },
  ]);
  const r = results.values().next().value;
  if (!r || r.status === "synced") {
    flash(`${fieldLabel} updated on ASA`, "success");
  } else if (r.status === "error") {
    flash(
      `${fieldLabel} change failed: ${r.errorMessage ?? "ASA rejected the request"}`,
      "error",
    );
  } else {
    flash(
      `${fieldLabel}: ASA hasn't confirmed yet — try again in a moment`,
      "error",
    );
  }
}

/** Какие запросы перечитать, когда подтвердилась заявка такого вида.
 *  Один список и для сброса, и для проверки «запрос ещё идёт». */
function keysForTypes(types: ReadonlySet<ResourceType>): string[][] {
  const keys: string[][] = [];
  if (types.has("ad_group")) {
    keys.push(["stats-ad-groups"], ["ad-groups-mirror"]);
  }
  if (types.has("keyword")) {
    keys.push(["stats-keywords"]);
    // A keyword status flip can change its parent ad group's serving
    // status — refresh the Ad Groups grain too.
    keys.push(["stats-ad-groups"]);
  }
  if (types.has("negative_keyword")) keys.push(["negative-keywords"]);
  if (types.has("ad")) {
    // Заявка на объявление — это Apply CPP: сменилась страница,
    // которую показывает группа. Смотрит на это вкладка Ads.
    keys.push(["campaign-cpps"], ["ads"]);
  }
  // Always refresh campaigns when ANY grain resolved: a child status
  // flip (enable a keyword / ad group) can move the parent campaign's
  // serving status (ON_HOLD → RUNNING), and the drainer re-fetches
  // that from ASA on confirm. Without this the Campaign Detail header
  // (fed by ["stats-campaigns"]) keeps showing a stale status until
  // the next collector pass. See pending_writes_drainer
  // _refresh_parent_campaign_serving.
  if (types.has("campaign") || types.has("ad_group") || types.has("keyword")) {
    keys.push(["stats-campaigns"]);
  }
  return keys;
}

function invalidateForTypes(
  qc: QueryClient,
  types: ReadonlySet<ResourceType>,
): void {
  for (const queryKey of keysForTypes(types)) {
    void qc.invalidateQueries({ queryKey });
  }
}

/** Сброс кеша не чаще раза в столько, и не поверх идущего запроса. */
export const INVALIDATE_GAP_MS = 4000;

const invalidators = new WeakMap<
  QueryClient,
  CoalescedInvalidator<ResourceType>
>();

/** Все сбросы кеша по подтверждённым заявкам идут через пачку. */
function scheduleInvalidate(
  qc: QueryClient,
  types: ReadonlySet<ResourceType>,
): void {
  if (types.size === 0) return;
  let inv = invalidators.get(qc);
  if (!inv) {
    inv = createCoalescedInvalidator<ResourceType>({
      flush: (t) => invalidateForTypes(qc, t),
      isBusy: (t) =>
        keysForTypes(t).some((queryKey) => qc.isFetching({ queryKey }) > 0),
      gapMs: INVALIDATE_GAP_MS,
    });
    invalidators.set(qc, inv);
  }
  inv.add(types);
}
